import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

export type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  approved: 'success',
  active: 'success',
  completed: 'success',
  locked: 'success',
  pending: 'warning',
  submitted: 'warning',
  invited: 'warning',
  draft: 'neutral',
  rejected: 'error',
  cancelled: 'error',
  disabled: 'error',
  expired: 'error',
};

@Component({
  selector: 'app-status-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
  host: {
    '[class]': 'hostClasses()',
    'role': 'status',
  },
  template: `{{ label() || ('status.' + status()) | translate }}`,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 500;
      text-transform: capitalize;
    }

    :host.success {
      background-color: oklch(0.95 0.05 145);
      color: oklch(0.35 0.15 145);
    }

    :host.warning {
      background-color: oklch(0.95 0.08 85);
      color: oklch(0.4 0.15 85);
    }

    :host.error {
      background-color: oklch(0.95 0.05 25);
      color: oklch(0.4 0.18 25);
    }

    :host.info {
      background-color: oklch(0.95 0.05 250);
      color: oklch(0.4 0.15 250);
    }

    :host.neutral {
      background-color: oklch(0.92 0 0);
      color: oklch(0.4 0 0);
    }
  `,
})
export class StatusBadgeComponent {
  readonly status = input.required<string>();
  readonly label = input<string>();
  readonly variant = input<BadgeVariant>();

  protected readonly resolvedVariant = computed(() => {
    const explicitVariant = this.variant();
    if (explicitVariant) {
      return explicitVariant;
    }
    return STATUS_VARIANTS[this.status().toLowerCase()] ?? 'neutral';
  });

  protected readonly hostClasses = computed(() => this.resolvedVariant());
}
